import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { Map as MapIcon, Package, Camera, Filter, Layers, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

// Leaflet loaded via CDN in index.html
const L = window.L;

const EQUIP_COLORS = {
  module: '#3B82F6', inverter: '#F59E0B', structure: '#8B5CF6',
  transformer: '#EF4444', meter: '#10B981', cable: '#64748B',
  combiner_box: '#EC4899', tracker: '#06B6D4', other: '#94A3B8',
};

const PHOTO_COLORS = {
  installation: '#3B82F6', progress: '#10B981', delivery: '#8B5CF6',
  defect: '#EF4444', general: '#F59E0B',
};

const equipLabel = (t) => ({
  module: 'Módulo FV', inverter: 'Inversor', structure: 'Estructura',
  transformer: 'Transformador', meter: 'Medidor', cable: 'Cable',
  combiner_box: 'Caja comb.', tracker: 'Tracker', other: 'Otro',
}[t] || t || '?');

export default function MapView() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEquipment, setShowEquipment] = useState(true);
  const [showPhotos, setShowPhotos] = useState(true);
  const [stats, setStats] = useState({ equipment: 0, photos: 0 });

  // Init map
  useEffect(() => {
    if (!L || mapInstance.current) return;

    mapInstance.current = L.map(mapRef.current, {
      center: [40.4168, -3.7038], // Madrid default
      zoom: 6,
      zoomControl: false,
      attributionControl: false,
    });

    // Dark tile layer
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(mapInstance.current);

    // Dark mode: invertir tiles OSM
    document.querySelectorAll('.leaflet-tile-pane').forEach(el => {
      el.style.filter = 'invert(1) hue-rotate(180deg) brightness(0.8) contrast(1.2)';
    });

    // Zoom control top-right
    L.control.zoom({ position: 'topright' }).addTo(mapInstance.current);

    // Attribution bottom-right
    L.control.attribution({ position: 'bottomright', prefix: false })
      .addAttribution('© <a href="https://www.openstreetmap.org/copyright">OSM</a> · <a href="https://carto.com/">CARTO</a>')
      .addTo(mapInstance.current);

    markersLayer.current = L.layerGroup().addTo(mapInstance.current);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  // Load projects
  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || [])).catch(() => {});
  }, []);

  // Load geo data
  useEffect(() => {
    setLoading(true);
    const params = selectedProject ? { project_id: selectedProject } : {};
    api.get('/analytics/geo-data', { params })
      .then(r => {
        setGeoData(r.data);
        updateMarkers(r.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedProject]);

  // Update markers when filters change
  useEffect(() => {
    if (geoData) updateMarkers(geoData);
  }, [showEquipment, showPhotos]);

  const updateMarkers = (data) => {
    if (!markersLayer.current || !data?.features) return;
    markersLayer.current.clearLayers();

    let equipCount = 0, photoCount = 0;
    const bounds = [];

    data.features.forEach(f => {
      const props = f.properties;
      const [lng, lat] = f.geometry.coordinates;
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;

      if (props.featureType === 'equipment' && showEquipment) {
        equipCount++;
        const color = EQUIP_COLORS[props.type] || '#94A3B8';
        const marker = L.circleMarker([lat, lng], {
          radius: 7, fillColor: color, fillOpacity: 0.85,
          color: '#fff', weight: 1.5, opacity: 0.9,
        });

        marker.bindPopup(`
          <div style="font-family:system-ui;min-width:180px">
            <div style="font-weight:700;font-size:13px;margin-bottom:6px;color:${color}">
              ⚡ ${equipLabel(props.type)}
            </div>
            <div style="font-size:12px;line-height:1.6;color:#ddd">
              ${props.brand ? `<b>Marca:</b> ${props.brand} ${props.model || ''}<br>` : ''}
              ${props.serial_number ? `<b>S/N:</b> <code>${props.serial_number}</code><br>` : ''}
              ${props.status ? `<b>Estado:</b> ${props.status}<br>` : ''}
              ${props.location_zone ? `<b>Zona:</b> ${props.location_zone}<br>` : ''}
              <b>Proyecto:</b> ${props.project_code || ''}
            </div>
          </div>
        `, { className: 'dark-popup' });

        markersLayer.current.addLayer(marker);
        bounds.push([lat, lng]);
      }

      if (props.featureType === 'photo' && showPhotos) {
        photoCount++;
        const color = PHOTO_COLORS[props.photo_type] || '#F59E0B';
        const marker = L.circleMarker([lat, lng], {
          radius: 5, fillColor: color, fillOpacity: 0.7,
          color: color, weight: 2, opacity: 0.5,
        });

        marker.bindPopup(`
          <div style="font-family:system-ui;min-width:180px">
            <div style="font-weight:700;font-size:13px;margin-bottom:6px;color:${color}">
              📷 Foto: ${props.photo_type || 'general'}
            </div>
            <div style="font-size:12px;line-height:1.6;color:#ddd">
              ${props.description ? `${props.description}<br>` : ''}
              <b>Por:</b> ${props.uploaded_by_name || '-'}<br>
              <b>Fecha:</b> ${props.created_at ? new Date(props.created_at).toLocaleDateString('es') : '-'}<br>
              <b>Proyecto:</b> ${props.project_code || ''}
            </div>
            ${props.thumbnail_url ? `<img src="${props.thumbnail_url}" style="width:100%;max-height:120px;object-fit:cover;border-radius:4px;margin-top:6px">` : ''}
          </div>
        `, { className: 'dark-popup' });

        markersLayer.current.addLayer(marker);
        bounds.push([lat, lng]);
      }
    });

    setStats({ equipment: equipCount, photos: photoCount });

    // Fit bounds
    if (bounds.length > 0 && mapInstance.current) {
      if (bounds.length === 1) {
        mapInstance.current.setView(bounds[0], 17);
      } else {
        mapInstance.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
      }
    }
  };

  const fitAll = () => {
    if (!geoData?.features?.length) return;
    const bounds = geoData.features
      .map(f => f.geometry.coordinates)
      .filter(([lng, lat]) => lat && lng && !isNaN(lat) && !isNaN(lng))
      .map(([lng, lat]) => [lat, lng]);
    if (bounds.length > 0 && mapInstance.current) {
      mapInstance.current.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  return (
    <div className="map-page">
      {/* Top Controls */}
      <div className="map-controls">
        <div className="map-controls-left">
          <MapIcon size={20} style={{ color: 'var(--accent)' }} />
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>Mapa de Obra</h2>
          <select className="filter-select" value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            style={{ minWidth: 220 }}>
            <option value="">Todos los proyectos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
          </select>
        </div>

        <div className="map-controls-right">
          <button className={`map-filter-btn ${showEquipment ? 'active' : ''}`}
            onClick={() => setShowEquipment(!showEquipment)}>
            <Package size={14} /> Equipos ({stats.equipment})
          </button>
          <button className={`map-filter-btn ${showPhotos ? 'active' : ''}`}
            onClick={() => setShowPhotos(!showPhotos)}>
            <Camera size={14} /> Fotos ({stats.photos})
          </button>
          <button className="map-filter-btn" onClick={fitAll} title="Ajustar vista">
            <Maximize2 size={14} />
          </button>
        </div>
      </div>

      {/* Map Container */}
      <div className="map-container">
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

        {loading && (
          <div className="map-loading">
            <div className="spinner" /> Cargando datos...
          </div>
        )}

        {!loading && stats.equipment === 0 && stats.photos === 0 && (
          <div className="map-empty">
            <MapIcon size={32} />
            <p>No hay datos geolocalizados</p>
            <span>Los equipos y fotos con GPS aparecerán aquí</span>
          </div>
        )}

        {/* Legend */}
        <div className="map-legend">
          <h4><Layers size={14} /> Leyenda</h4>
          {showEquipment && (
            <div className="legend-section">
              <span className="legend-title">Equipos</span>
              {Object.entries(EQUIP_COLORS).slice(0, 6).map(([key, color]) => (
                <div key={key} className="legend-item">
                  <span className="legend-dot" style={{ background: color }} />
                  {equipLabel(key)}
                </div>
              ))}
            </div>
          )}
          {showPhotos && (
            <div className="legend-section">
              <span className="legend-title">Fotos</span>
              {Object.entries(PHOTO_COLORS).map(([key, color]) => (
                <div key={key} className="legend-item">
                  <span className="legend-ring" style={{ borderColor: color }} />
                  {key}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

